import { afterNextRender, Component, DestroyRef, ElementRef, inject, signal, viewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, ModalComponent, ModalFooterDirective } from 'mbs-ui-kit';
import { PlanMode } from 'mbs-ui-shared@models/PlanTypes.enum';
import { CompressionEncryptionStepValue } from 'wizards/models/compression-encription-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { CompressionEncryptionStepComponent } from 'wizards/steps/compression-encryption-step/compression-encryption-step.component';
import { CompanyBackupTemplate } from '../../models/company-wizard-models';
import { borrowPlanContext, planContextFromTemplate } from '../../utils/company-wizard-plan-context.util';

export type CompressionEncryptionModalData = {
  template: CompanyBackupTemplate;
};

export type CompressionEncryptionModalResult = CompressionEncryptionStepValue;

// Default change detection on purpose: the hosted step restores the password through a setTimeout and relies on the
// global tick reaching it, which an OnPush ancestor would cut off
@Component({
  imports: [ButtonComponent, CompressionEncryptionStepComponent, FormsModule, I18NextPipe, ModalComponent, ModalFooterDirective],
  selector: 'mbs-compression-encryption-modal',
  standalone: true,
  templateUrl: './compression-encryption-modal.component.html'
})
export class CompressionEncryptionModalComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly mainService = inject(RemoteManagementWizardsService);

  protected readonly baseModal = viewChild(ModalComponent);
  protected readonly stepComponent = viewChild(CompressionEncryptionStepComponent);

  public readonly isS3Selected: WritableSignal<boolean> = signal(false);
  public readonly ready: WritableSignal<boolean> = signal(false);
  public readonly value: WritableSignal<CompressionEncryptionStepValue> = signal(null);

  constructor() {
    // ModalService assigns data only after open() returns, and a signal viewChild is unresolved until the view is checked
    afterNextRender(() => this.applyModalData());
  }

  public saveClickHandler(): void {
    // forceValid dereferences its argument despite the null default, so it must be given one
    this.stepComponent()?.forceValid({ isSave: true });

    if (this.value()?.valid) return void this.baseModal().save(this.value() as CompressionEncryptionModalResult);

    this.revealPasswordErrors();
  }

  public cancelClickHandler(): void {
    this.baseModal().close();
  }

  // Private section
  // The password fields belong to ConfirmPasswordComponent's own form, which forceValid does not reach, and they show errors only
  // once dirty; replaying the input event marks them dirty the way typing does, without changing what was entered
  private revealPasswordErrors(): void {
    this.elementRef.nativeElement
      .querySelectorAll<HTMLInputElement>('mbs-confirm-password input:enabled')
      .forEach((input) => input.dispatchEvent(new Event('input')));
  }

  private applyModalData(): void {
    const data = this.baseModal()?.data as CompressionEncryptionModalData;

    if (!data) return;

    // Edit mode, because we are reopening an existing template: only there does the step fill InitialValue and
    // restore the password into the confirm-password control. Its edit-only side effects are inert under RD + NBF.
    this.destroyRef.onDestroy(borrowPlanContext(this.mainService, planContextFromTemplate(data.template, PlanMode.edit)));

    this.isS3Selected.set(!!data.template.advancedOptionsFlags?.UseS3AccelerationPossible);
    this.value.set(data.template.compressionEncryptionStep);
    this.ready.set(true);
  }
}
